import React, { useState, useEffect, useContext } from 'react';
import {
  User, Clock, CheckCircle2, XCircle, AlertCircle, Info, CreditCard, MapPin,
  DollarSign, TrendingUp, RefreshCw, Filter, Calendar, Package, ChevronDown,
  ChevronUp, Banknote, ArrowDownCircle, Edit3, Save, X, Building2, Phone,
  Mail, Shield, FileText, Wallet, Globe, Hash, ExternalLink, Lock,
  Upload, RotateCcw, AlertTriangle
} from 'lucide-react';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

const DesignerProfile = () => {
  const { authToken } = useContext(AuthContext);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentFilters, setPaymentFilters] = useState({
    status: 'all',
    fromDate: '',
    toDate: ''
  });
  const [isPayoutsExpanded, setIsPayoutsExpanded] = useState(true);

  // Address editing state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);

  // Bank editing state
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_account_number: '', ifsc_code: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [bankIfscValidation, setBankIfscValidation] = useState({ loading: false, valid: null, bankName: '', branch: '' });
  const [syncingFromRazorpay, setSyncingFromRazorpay] = useState(false);

  // KYC resubmission state
  const [kycRequirements, setKycRequirements] = useState(null);
  const [showKycResubmitForm, setShowKycResubmitForm] = useState(false);
  const [kycResubmitForm, setKycResubmitForm] = useState({
    name: '', email: '', phone: '', pan: '',
    bank_account_number: '', ifsc_code: '',
    street_address_1: '', street_address_2: '', city: '', state: '', postal_code: ''
  });
  const [isResubmittingKyc, setIsResubmittingKyc] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [retryingOnboarding, setRetryingOnboarding] = useState(false);

  useEffect(() => {
    fetchDesignerData();
  }, [authToken]);

  useEffect(() => {
    if (profileData) {
      fetchPayments();
      // Initialize address form with current data
      setAddressForm({
        street_address_1: profileData.street_address_1 || '',
        street_address_2: profileData.street_address_2 || '',
        city: profileData.city || '',
        state: profileData.state || '',
        postal_code: profileData.postal_code || '',
        country: profileData.country || 'IN'
      });
      // Auto-fetch KYC requirements when status is needs_clarification
      if (profileData.onboarding_status === 'needs_clarification') {
        fetchKycRequirements();
      }
    }
  }, [profileData, paymentFilters]);

  const fetchDesignerData = async () => {
    try {
      setLoading(true);
      const profileResponse = await axios.get(`${API_BASE_URL}/api/designer/profile/`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });
      setProfileData(profileResponse.data.data);
    } catch (error) {
      console.error('Error fetching designer data:', error);
      if (error.response?.status === 404) {
        Swal.fire({
          title: 'No Designer Profile',
          text: 'Please complete your KYC first to view your designer profile.',
          icon: 'info',
          confirmButtonColor: '#ea580c'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentLoading(true);
      const params = new URLSearchParams();
      if (paymentFilters.status !== 'all') params.append('status', paymentFilters.status);
      if (paymentFilters.fromDate) params.append('from_date', new Date(paymentFilters.fromDate).getTime() / 1000);
      if (paymentFilters.toDate) params.append('to_date', new Date(paymentFilters.toDate).getTime() / 1000);

      const response = await axios.get(`${API_BASE_URL}/api/designer/payments/?${params}`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setPayments(response.data.data.transfers);
        setPaymentStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSaveAddress = async () => {
    try {
      setSavingAddress(true);
      const response = await axios.put(
        `${API_BASE_URL}/api/designer/profile/update/`,
        addressForm,
        {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setProfileData(response.data.data);
        setIsEditingAddress(false);
        Swal.fire({
          title: 'Success!',
          text: 'Address updated successfully',
          icon: 'success',
          confirmButtonColor: '#ea580c',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating address:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update address',
        icon: 'error',
        confirmButtonColor: '#ea580c'
      });
    } finally {
      setSavingAddress(false);
    }
  };

  const cancelAddressEdit = () => {
    setAddressForm({
      street_address_1: profileData.street_address_1 || '',
      street_address_2: profileData.street_address_2 || '',
      city: profileData.city || '',
      state: profileData.state || '',
      postal_code: profileData.postal_code || '',
      country: profileData.country || 'IN'
    });
    setIsEditingAddress(false);
  };

  // Bank editing handlers
  const startBankEdit = () => {
    setBankForm({ bank_account_number: '', ifsc_code: '' });
    setBankIfscValidation({ loading: false, valid: null, bankName: '', branch: '' });
    setIsEditingBank(true);
  };

  const cancelBankEdit = () => {
    setBankForm({ bank_account_number: '', ifsc_code: '' });
    setBankIfscValidation({ loading: false, valid: null, bankName: '', branch: '' });
    setIsEditingBank(false);
  };

  const validateIfscCode = async (ifsc) => {
    if (!ifsc || ifsc.length !== 11) {
      setBankIfscValidation({ loading: false, valid: null, bankName: '', branch: '' });
      return;
    }
    setBankIfscValidation({ loading: true, valid: null, bankName: '', branch: '' });
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/designer/validate/ifsc/`,
        { ifsc_code: ifsc },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.valid) {
        setBankIfscValidation({
          loading: false,
          valid: true,
          bankName: response.data.bank_name || '',
          branch: response.data.branch || ''
        });
      } else {
        setBankIfscValidation({ loading: false, valid: false, bankName: '', branch: '' });
      }
    } catch {
      setBankIfscValidation({ loading: false, valid: false, bankName: '', branch: '' });
    }
  };

  const handleSaveBankDetails = async () => {
    const { bank_account_number, ifsc_code } = bankForm;

    if (!bank_account_number || !/^\d{9,18}$/.test(bank_account_number)) {
      Swal.fire({ title: 'Invalid Account Number', text: 'Bank account number must be 9-18 digits.', icon: 'error', confirmButtonColor: '#ea580c' });
      return;
    }
    if (!ifsc_code || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc_code.toUpperCase())) {
      Swal.fire({ title: 'Invalid IFSC', text: 'Please enter a valid IFSC code.', icon: 'error', confirmButtonColor: '#ea580c' });
      return;
    }

    const result = await Swal.fire({
      title: 'Update Bank Details?',
      html: `<p>Are you sure you want to update your bank account?</p><p class="text-sm text-gray-500 mt-2">New Account: ****${bank_account_number.slice(-4)}<br/>New IFSC: ${ifsc_code.toUpperCase()}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Update',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setSavingBank(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/designer/profile/update-bank-details/`,
        { bank_account_number, ifsc_code: ifsc_code.toUpperCase() },
        {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setProfileData(response.data.data);
        setIsEditingBank(false);
        Swal.fire({ title: 'Success!', text: 'Bank details updated successfully.', icon: 'success', confirmButtonColor: '#ea580c', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to update bank details.', icon: 'error', confirmButtonColor: '#ea580c' });
    } finally {
      setSavingBank(false);
    }
  };

  const handleSyncFromRazorpay = async () => {
    try {
      setSyncingFromRazorpay(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/designer/profile/sync-status/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setProfileData(response.data.data);
        if (response.data.bank_details_synced) {
          Swal.fire({ title: 'Bank Details Synced!', text: 'Your bank details have been updated from Razorpay.', icon: 'success', confirmButtonColor: '#ea580c' });
        } else {
          Swal.fire({ title: 'Synced', text: 'Profile synced. No bank detail changes detected from Razorpay.', icon: 'info', confirmButtonColor: '#ea580c', timer: 2500, showConfirmButton: false });
        }
      }
    } catch (error) {
      Swal.fire({ title: 'Sync Failed', text: error.response?.data?.message || 'Failed to sync from Razorpay.', icon: 'error', confirmButtonColor: '#ea580c' });
    } finally {
      setSyncingFromRazorpay(false);
    }
  };

  const fetchKycRequirements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/designer/kyc/requirements/`, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data.success) {
        setKycRequirements(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching KYC requirements:', error);
    }
  };

  const handleResubmitKyc = async () => {
    // Filter out empty fields
    const payload = {};
    Object.entries(kycResubmitForm).forEach(([key, value]) => {
      if (value && value.trim()) payload[key] = value.trim();
    });

    if (Object.keys(payload).length === 0) {
      Swal.fire({ title: 'No Changes', text: 'Please fill in at least one field to resubmit.', icon: 'warning', confirmButtonColor: '#ea580c' });
      return;
    }

    try {
      setIsResubmittingKyc(true);
      const response = await axios.post(`${API_BASE_URL}/api/designer/kyc/resubmit/`, payload, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        Swal.fire({ title: 'KYC Resubmitted!', text: 'Your updated details have been sent to Razorpay for review.', icon: 'success', confirmButtonColor: '#ea580c' });
        setShowKycResubmitForm(false);
        setProfileData(response.data.data);
        fetchKycRequirements();
      } else {
        Swal.fire({ title: 'Partial Success', text: response.data.message || 'Some updates may have failed.', icon: 'warning', confirmButtonColor: '#ea580c' });
      }
    } catch (error) {
      Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to resubmit KYC.', icon: 'error', confirmButtonColor: '#ea580c' });
    } finally {
      setIsResubmittingKyc(false);
    }
  };

  const handleUploadDocument = async (docType, file) => {
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ title: 'File Too Large', text: 'Maximum file size is 5MB.', icon: 'error', confirmButtonColor: '#ea580c' });
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({ title: 'Invalid File', text: 'Only JPEG, PNG, and PDF files are allowed.', icon: 'error', confirmButtonColor: '#ea580c' });
      return;
    }

    try {
      setUploadingDocument(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);

      const response = await axios.post(`${API_BASE_URL}/api/designer/kyc/upload-document/`, formData, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        Swal.fire({ title: 'Uploaded!', text: response.data.message, icon: 'success', confirmButtonColor: '#ea580c', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      Swal.fire({ title: 'Upload Failed', text: error.response?.data?.message || 'Failed to upload document.', icon: 'error', confirmButtonColor: '#ea580c' });
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleRetryFullOnboarding = async () => {
    const result = await Swal.fire({
      title: 'Start Fresh KYC?',
      html: '<p>This will create a completely new Razorpay account and restart the KYC process from scratch.</p><p class="text-sm text-gray-500 mt-2">Your previous Razorpay account will be abandoned.</p>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Start Fresh',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setRetryingOnboarding(true);
      const response = await axios.post(`${API_BASE_URL}/api/designer/kyc/retry-onboarding/`, {}, {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        Swal.fire({ title: 'Fresh Start!', text: 'New Razorpay account created. KYC is now under review.', icon: 'success', confirmButtonColor: '#ea580c' });
        setProfileData(response.data.data);
      }
    } catch (error) {
      Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to retry onboarding.', icon: 'error', confirmButtonColor: '#ea580c' });
    } finally {
      setRetryingOnboarding(false);
    }
  };

  const openKycResubmitForm = () => {
    setKycResubmitForm({
      name: profileData.name || '',
      email: profileData.email || '',
      phone: profileData.phone || '',
      pan: profileData.pan || '',
      bank_account_number: '',
      ifsc_code: profileData.ifsc_code || '',
      street_address_1: profileData.street_address_1 || '',
      street_address_2: profileData.street_address_2 || '',
      city: profileData.city || '',
      state: profileData.state || '',
      postal_code: profileData.postal_code || '',
    });
    setShowKycResubmitForm(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
      under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Under Review' },
      needs_clarification: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle, label: 'Needs Clarification' },
      pending: { bg: 'bg-gray-100', text: 'text-gray-700', icon: AlertCircle, label: 'Pending' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (displayStatus) => {
    if (displayStatus === 'settled') {
      return (
        <div className="flex flex-col">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-700">
            <CheckCircle2 className="w-4 h-4" />
            Money Received
          </span>
          <span className="text-xs text-green-600 mt-1">Credited to your bank</span>
        </div>
      );
    }
    return (
      <div className="flex flex-col">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
          <Clock className="w-4 h-4" />
          In Progress
        </span>
        <span className="text-xs text-yellow-600 mt-1">Will be credited soon</span>
      </div>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh'
  ];

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin border-t-orange-500 mx-auto"></div>
              <User className="w-6 h-6 text-orange-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading your profile...</p>
          </div>
        </div>
      } />
    );
  }

  if (!profileData) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <User className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Complete Your KYC</h2>
            <p className="text-gray-600 mb-6">To access your designer profile and receive payouts, please complete the KYC verification process.</p>
            <button
              onClick={() => window.location.href = '/designer-onboarding'}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200"
            >
              Start KYC Verification
            </button>
          </div>
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Profile Header Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-orange-500 via-orange-500 to-amber-500 px-6 py-8 sm:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white">{profileData.name}</h1>
                    <p className="text-orange-100 flex items-center gap-2 mt-1">
                      <Mail className="w-4 h-4" /> {profileData.email}
                    </p>
                    <p className="text-orange-100 flex items-center gap-2">
                      <Phone className="w-4 h-4" /> {profileData.phone}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-2">
                  {getStatusBadge(profileData.onboarding_status)}
                  <p className="text-orange-100 text-sm">Member since {formatDate(profileData.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
              <div className="p-4 sm:p-6 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-green-600">
                  {(paymentStats?.total_settled || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Money Received</p>
              </div>
              <div className="p-4 sm:p-6 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-yellow-600">
                  {(paymentStats?.total_processing || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">In Progress</p>
              </div>
              <div className="p-4 sm:p-6 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                  {paymentStats?.transfer_count || 0}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Total Transfers</p>
              </div>
              <div className="p-4 sm:p-6 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-orange-600 capitalize">
                  {profileData.business_type || 'Individual'}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Account Type</p>
              </div>
            </div>
          </div>

          {/* KYC Needs Clarification Card */}
          {profileData.onboarding_status === 'needs_clarification' && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-900">KYC Needs Clarification</h3>
                  <p className="text-amber-700 text-sm mt-1">Razorpay requires you to fix certain details before your account can be activated.</p>

                  {/* Requirements list */}
                  {kycRequirements?.requirements && kycRequirements.requirements.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-amber-800">Issues to fix:</p>
                      {kycRequirements.requirements.map((req, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2">
                          <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="text-sm text-amber-900">{req.description}</span>
                          {req.reason_code && (
                            <span className="text-xs text-amber-600 ml-auto">({req.reason_code})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={openKycResubmitForm}
                      className="px-5 py-2.5 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition-colors flex items-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Fix & Resubmit KYC
                    </button>
                    <button
                      onClick={fetchKycRequirements}
                      className="px-4 py-2.5 border border-amber-400 text-amber-700 font-medium rounded-xl hover:bg-amber-100 transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Status Card */}
          {profileData.onboarding_status === 'rejected' && (
            <div className="bg-red-50 border-2 border-red-300 rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-900">KYC Rejected</h3>
                  <p className="text-red-700 text-sm mt-1">Your Razorpay account verification was permanently rejected. You can start a fresh KYC process with a new Razorpay account.</p>
                  {profileData.admin_notes && (
                    <p className="text-red-600 text-xs mt-2 bg-red-100 rounded-lg px-3 py-2">
                      <strong>Note:</strong> {profileData.admin_notes}
                    </p>
                  )}
                  <button
                    onClick={handleRetryFullOnboarding}
                    disabled={retryingOnboarding}
                    className="mt-4 px-5 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {retryingOnboarding ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    {retryingOnboarding ? 'Creating New Account...' : 'Start Fresh KYC'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KYC Resubmit Modal */}
          {showKycResubmitForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-amber-500 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
                  <h3 className="text-lg font-bold">Fix & Resubmit KYC Details</h3>
                  <button onClick={() => setShowKycResubmitForm(false)} className="p-1 hover:bg-amber-600 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-5">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Tip:</strong> Only fill in the fields you need to correct. Leave others empty to keep current values.
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Full Name (as per PAN)</label>
                      <input type="text" value={kycResubmitForm.name} onChange={(e) => setKycResubmitForm({...kycResubmitForm, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.name} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">PAN Number</label>
                      <input type="text" value={kycResubmitForm.pan} onChange={(e) => setKycResubmitForm({...kycResubmitForm, pan: e.target.value.toUpperCase()})}
                        maxLength={10} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm font-mono" placeholder={profileData.pan} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Email</label>
                      <input type="email" value={kycResubmitForm.email} onChange={(e) => setKycResubmitForm({...kycResubmitForm, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.email} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</label>
                      <input type="tel" value={kycResubmitForm.phone} onChange={(e) => setKycResubmitForm({...kycResubmitForm, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.phone} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Bank Account Number</label>
                      <input type="text" value={kycResubmitForm.bank_account_number} onChange={(e) => setKycResubmitForm({...kycResubmitForm, bank_account_number: e.target.value.replace(/\D/g, '')})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm font-mono" placeholder="New account number (if changing)" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">IFSC Code</label>
                      <input type="text" value={kycResubmitForm.ifsc_code} onChange={(e) => setKycResubmitForm({...kycResubmitForm, ifsc_code: e.target.value.toUpperCase()})}
                        maxLength={11} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm font-mono" placeholder={profileData.ifsc_code} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Street Address</label>
                    <input type="text" value={kycResubmitForm.street_address_1} onChange={(e) => setKycResubmitForm({...kycResubmitForm, street_address_1: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.street_address_1} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">City</label>
                      <input type="text" value={kycResubmitForm.city} onChange={(e) => setKycResubmitForm({...kycResubmitForm, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.city} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">State</label>
                      <input type="text" value={kycResubmitForm.state} onChange={(e) => setKycResubmitForm({...kycResubmitForm, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.state} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Postal Code</label>
                      <input type="text" value={kycResubmitForm.postal_code} onChange={(e) => setKycResubmitForm({...kycResubmitForm, postal_code: e.target.value})}
                        maxLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" placeholder={profileData.postal_code} />
                    </div>
                  </div>

                  {/* Document Upload Section */}
                  <div className="border-t border-gray-200 pt-5">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload Documents (Optional)
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { type: 'aadhar_front', label: 'Aadhaar Front' },
                        { type: 'aadhar_back', label: 'Aadhaar Back' },
                        { type: 'cancelled_cheque', label: 'Cancelled Cheque' },
                      ].map(doc => (
                        <div key={doc.type} className="border border-dashed border-gray-300 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600 mb-2">{doc.label}</p>
                          <label className="cursor-pointer flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-600 transition-colors">
                            <Upload className="w-4 h-4" />
                            {uploadingDocument ? 'Uploading...' : 'Choose File'}
                            <input
                              type="file"
                              className="hidden"
                              accept="image/jpeg,image/png,application/pdf"
                              disabled={uploadingDocument}
                              onChange={(e) => {
                                if (e.target.files[0]) handleUploadDocument(doc.type, e.target.files[0]);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, PDF (max 5MB)</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setShowKycResubmitForm(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                      Cancel
                    </button>
                    <button onClick={handleResubmitKyc} disabled={isResubmittingKyc}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      {isResubmittingKyc ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isResubmittingKyc ? 'Submitting...' : 'Resubmit KYC'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column - Profile Details */}
            <div className="lg:col-span-1 space-y-6">

              {/* Personal Details Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Business Name</p>
                      <p className="text-gray-900 font-medium">{profileData.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                      <p className="text-gray-900 font-medium break-all">{profileData.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                      <p className="text-gray-900 font-medium">{profileData.phone}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax & Banking Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Tax & Banking</h3>
                  </div>
                  {profileData.bank_details_submitted && profileData.razorpay_activation_status === 'activated' && !isEditingBank && (
                    <button
                      onClick={startBankEdit}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      Update Bank
                    </button>
                  )}
                </div>

                {/* Tax info (always shown) */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">PAN</p>
                      <div className="flex items-center gap-2">
                        <p className="text-gray-900 font-medium font-mono">{profileData.pan || 'Not provided'}</p>
                        <Lock className="w-3.5 h-3.5 text-gray-400" title="PAN cannot be changed" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">GSTIN</p>
                      <p className="text-gray-900 font-medium font-mono">{profileData.gstin || 'Not registered'}</p>
                    </div>
                  </div>
                </div>

                {/* Bank details - display or edit mode */}
                {!isEditingBank ? (
                  <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-start gap-3">
                      <Wallet className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Bank Account</p>
                        <p className="text-gray-900 font-medium font-mono">
                          {profileData.bank_account_number ? `****${profileData.bank_account_number.slice(-4)}` : 'Not provided'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">IFSC Code</p>
                        <p className="text-gray-900 font-medium font-mono">{profileData.ifsc_code || 'Not provided'}</p>
                      </div>
                    </div>
                    {profileData.last_bank_update && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last updated: {new Date(profileData.last_bank_update).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                        {profileData.bank_update_count > 0 && ` (${profileData.bank_update_count} update${profileData.bank_update_count > 1 ? 's' : ''})`}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Way 1: Edit bank details form */
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                    <p className="text-sm text-gray-600 font-medium">Update Bank Details</p>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Bank Account Number</label>
                      <input
                        type="text"
                        value={bankForm.bank_account_number}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 18);
                          setBankForm({ ...bankForm, bank_account_number: val });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                        placeholder="9-18 digit account number"
                      />
                      {bankForm.bank_account_number && (bankForm.bank_account_number.length < 9 || bankForm.bank_account_number.length > 18) && (
                        <p className="text-xs text-red-500 mt-1">Must be 9-18 digits</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">IFSC Code</label>
                      <input
                        type="text"
                        value={bankForm.ifsc_code}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase().slice(0, 11);
                          setBankForm({ ...bankForm, ifsc_code: val });
                          if (val.length === 11) validateIfscCode(val);
                          else setBankIfscValidation({ loading: false, valid: null, bankName: '', branch: '' });
                        }}
                        maxLength={11}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono ${
                          bankIfscValidation.valid === true ? 'border-green-400' : bankIfscValidation.valid === false ? 'border-red-400' : 'border-gray-300'
                        }`}
                        placeholder="e.g. SBIN0001234"
                      />
                      {bankIfscValidation.loading && (
                        <p className="text-xs text-gray-500 mt-1">Validating IFSC...</p>
                      )}
                      {bankIfscValidation.valid === true && (
                        <p className="text-xs text-green-600 mt-1">
                          {bankIfscValidation.bankName}{bankIfscValidation.branch ? ` - ${bankIfscValidation.branch}` : ''}
                        </p>
                      )}
                      {bankIfscValidation.valid === false && (
                        <p className="text-xs text-red-500 mt-1">Invalid IFSC code</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-gray-400" />
                        <p className="text-xs text-gray-500">PAN: <span className="font-mono font-medium text-gray-700">{profileData.pan}</span> (cannot be changed)</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={cancelBankEdit}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveBankDetails}
                        disabled={savingBank}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {savingBank ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {savingBank ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Way 2: Razorpay Dashboard section */}
                {profileData.bank_details_submitted && profileData.razorpay_activation_status === 'activated' && (
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="text-xs font-semibold text-gray-400 uppercase">or</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Manage on Razorpay Dashboard</p>
                    <p className="text-xs text-gray-500 mb-3">
                      You can also update your bank details directly on Razorpay's dashboard, then sync back here.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <a
                        href="https://dashboard.razorpay.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open Razorpay
                      </a>
                      <button
                        onClick={handleSyncFromRazorpay}
                        disabled={syncingFromRazorpay}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200 disabled:opacity-50"
                      >
                        {syncingFromRazorpay ? (
                          <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        {syncingFromRazorpay ? 'Syncing...' : 'Sync from Razorpay'}
                      </button>
                    </div>
                    <details className="text-xs text-gray-500">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">How to change bank on Razorpay?</summary>
                      <ol className="mt-2 space-y-1 list-decimal list-inside text-gray-500">
                        <li>Log in to Razorpay Dashboard with your registered email</li>
                        <li>Go to Account & Settings</li>
                        <li>Navigate to Bank Account Details</li>
                        <li>Click "Change Bank Account"</li>
                        <li>Enter new bank details and verify with OTP</li>
                        <li>Upload a cancelled cheque or bank statement</li>
                        <li>Come back here and click "Sync from Razorpay"</li>
                      </ol>
                    </details>
                  </div>
                )}
              </div>

              {/* Business Address Card - EDITABLE */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Business Address</h3>
                  </div>
                  {!isEditingAddress ? (
                    <button
                      onClick={() => setIsEditingAddress(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={cancelAddressEdit}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleSaveAddress}
                        disabled={savingAddress}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {savingAddress ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {!isEditingAddress ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-900">
                          {profileData.street_address_1 || 'No address provided'}
                        </p>
                        {profileData.street_address_2 && (
                          <p className="text-gray-600">{profileData.street_address_2}</p>
                        )}
                        <p className="text-gray-600">
                          {[profileData.city, profileData.state, profileData.postal_code].filter(Boolean).join(', ')}
                        </p>
                        <p className="text-gray-500">{profileData.country || 'India'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Street Address</label>
                      <input
                        type="text"
                        value={addressForm.street_address_1}
                        onChange={(e) => setAddressForm({ ...addressForm, street_address_1: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                        placeholder="Building, Street name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Address Line 2</label>
                      <input
                        type="text"
                        value={addressForm.street_address_2}
                        onChange={(e) => setAddressForm({ ...addressForm, street_address_2: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                        placeholder="Area, Landmark (optional)"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">City</label>
                        <input
                          type="text"
                          value={addressForm.city}
                          onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Postal Code</label>
                        <input
                          type="text"
                          value={addressForm.postal_code}
                          onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                          maxLength={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                          placeholder="6 digits"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">State</label>
                      <select
                        value={addressForm.state}
                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      >
                        <option value="">Select State</option>
                        {indianStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Razorpay Status Card */}
              {profileData.razorpay_account_id && (
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-lg p-6 border border-orange-100">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Payment Gateway</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        profileData.razorpay_activation_status === 'activated'
                          ? 'bg-green-100 text-green-700'
                          : profileData.razorpay_activation_status === 'needs_clarification'
                          ? 'bg-amber-100 text-amber-700'
                          : profileData.razorpay_activation_status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : profileData.razorpay_activation_status === 'suspended'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {profileData.razorpay_activation_status === 'activated' ? 'Active'
                          : profileData.razorpay_activation_status === 'needs_clarification' ? 'Needs Fix'
                          : profileData.razorpay_activation_status === 'rejected' ? 'Rejected'
                          : profileData.razorpay_activation_status === 'suspended' ? 'Suspended'
                          : 'Pending'}
                      </span>
                    </div>
                    {profileData.funds_on_hold && (
                      <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-medium text-red-700">Funds on hold</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-orange-200">
                      <p className="text-xs text-gray-500 font-mono truncate">ID: {profileData.razorpay_account_id}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Payouts */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">

                {/* Payouts Header */}
                <button
                  onClick={() => setIsPayoutsExpanded(!isPayoutsExpanded)}
                  className="w-full px-6 py-5 flex items-center justify-between bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-bold">Payouts & Transactions</h2>
                      <p className="text-green-100 text-sm">Track your earnings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-2xl font-bold">
                        {(paymentStats?.total_settled || 0).toLocaleString('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0})}
                      </p>
                      <p className="text-green-100 text-sm">Total Received</p>
                    </div>
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      {isPayoutsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                </button>

                {/* Payouts Content */}
                {isPayoutsExpanded && (
                  <div className="p-6 space-y-6">
                    {paymentLoading ? (
                      <div className="py-16 text-center">
                        <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-500 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Loading transactions...</p>
                      </div>
                    ) : (
                      <>
                        {/* Stats Summary */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
                            <ArrowDownCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                            <p className="text-xl sm:text-2xl font-bold text-green-700">
                              {(paymentStats?.total_settled || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                            </p>
                            <p className="text-xs sm:text-sm text-green-600 font-medium">Received</p>
                          </div>
                          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 text-center border border-yellow-200">
                            <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                            <p className="text-xl sm:text-2xl font-bold text-yellow-700">
                              {(paymentStats?.total_processing || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                            </p>
                            <p className="text-xs sm:text-sm text-yellow-600 font-medium">In Transit</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
                            <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                            <p className="text-xl sm:text-2xl font-bold text-blue-700">
                              {paymentStats?.transfer_count || 0}
                            </p>
                            <p className="text-xs sm:text-sm text-blue-600 font-medium">Transfers</p>
                          </div>
                        </div>

                        {/* Filters */}
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Filter className="w-4 h-4" />
                              <span className="font-medium text-sm">Filters</span>
                            </div>
                            <button
                              onClick={fetchPayments}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Refresh
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <select
                              value={paymentFilters.status}
                              onChange={(e) => setPaymentFilters({ ...paymentFilters, status: e.target.value })}
                              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              <option value="all">All Status</option>
                              <option value="settled">Money Received</option>
                              <option value="processing">In Progress</option>
                            </select>
                            <input
                              type="date"
                              value={paymentFilters.fromDate}
                              onChange={(e) => setPaymentFilters({ ...paymentFilters, fromDate: e.target.value })}
                              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="From"
                            />
                            <input
                              type="date"
                              value={paymentFilters.toDate}
                              onChange={(e) => setPaymentFilters({ ...paymentFilters, toDate: e.target.value })}
                              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="To"
                            />
                          </div>
                        </div>

                        {/* Transactions List */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                              <Package className="w-5 h-5 text-gray-400" />
                              Transactions
                            </h4>
                            <span className="text-sm text-gray-500">{payments.length} records</span>
                          </div>

                          {payments.length === 0 ? (
                            <div className="text-center py-16 bg-gray-50 rounded-xl">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <DollarSign className="w-8 h-8 text-gray-400" />
                              </div>
                              <p className="text-gray-600 font-medium">No transactions yet</p>
                              <p className="text-gray-400 text-sm mt-1">Your payouts will appear here</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {payments.map((payment) => (
                                <div
                                  key={payment.id}
                                  className={`rounded-xl p-4 border-2 transition-all hover:shadow-md ${
                                    payment.display_status === 'settled'
                                      ? 'bg-green-50 border-green-200'
                                      : 'bg-yellow-50 border-yellow-200'
                                  }`}
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                        payment.display_status === 'settled' ? 'bg-green-100' : 'bg-yellow-100'
                                      }`}>
                                        {payment.display_status === 'settled' ? (
                                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        ) : (
                                          <Clock className="w-5 h-5 text-yellow-600" />
                                        )}
                                      </div>
                                      <div>
                                        {getPaymentStatusBadge(payment.display_status)}
                                        <p className="text-xs text-gray-500 mt-2 font-mono">ID: {payment.id}</p>
                                        <p className="text-xs text-gray-500">{formatTimestamp(payment.created_at)}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className={`text-2xl font-bold ${
                                        payment.display_status === 'settled' ? 'text-green-700' : 'text-yellow-700'
                                      }`}>
                                        {payment.amount?.toLocaleString('en-IN', {style: 'currency', currency: 'INR'})}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                          <div className="flex gap-3">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium mb-1">Understanding Payouts</p>
                              <ul className="space-y-0.5 text-blue-700">
                                <li><span className="text-green-600 font-medium">Money Received</span> - Credited to your bank</li>
                                <li><span className="text-yellow-600 font-medium">In Progress</span> - Will arrive in 2-3 business days</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    } />
  );
};

export default DesignerProfile;
